import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";

import Footer from "../components/footer";
import Navbar from "../components/navbar";
import ScrollToTop from "../components/scroll-to-top";

import hero from "../assets/images/hero/pages.jpg";
import team1 from "../assets/images/team/Esther.jpeg";

import { blogData } from "../data/data";

// Twitter embed loader
function useTwitterEmbed() {
  useEffect(() => {
    if (window.twttr) {
      window.twttr.widgets.load();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://platform.twitter.com/widgets.js";
    script.async = true;
    script.charset = "utf-8";
    document.body.appendChild(script);
    return () => {
      // leave the script tag — removing it breaks subsequent loads
    };
  }, []);
}

// Press detail layout
function PressDetail({ data }) {
  useTwitterEmbed();

  return (
    <section className="section">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8 col-12">
            {/* Hero image */}
            <div className="card border-0 shadow rounded overflow-hidden mb-4">
              <img
                src={data.image}
                className="img-fluid w-100"
                style={{ maxHeight: 420, objectFit: "cover" }}
                alt={data.title}
              />
            </div>

            {/* Press source banner */}
            <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
              <span className="badge bg-danger fs-6 px-3 py-2">
                <i className="mdi mdi-newspaper me-1"></i> Press Feature
              </span>
              <span className="text-muted">
                As featured in <strong>{data.source}</strong> · {data.date}
              </span>
            </div>

            {/* Article summary */}
            <div className="card border-0 shadow-sm rounded p-4 mb-4">
              <h4 className="fw-semibold mb-3">{data.title}</h4>
              <p
                className="text-muted mb-4"
                style={{ fontSize: "1.05rem", lineHeight: 1.7 }}
              >
                {data.desc}
              </p>

              {/* Read on source CTA */}
              <div
                className="alert border d-flex align-items-start gap-3 mb-0"
                style={{ background: "#fafafa" }}
              >
                <i className="mdi mdi-open-in-new mdi-24px text-danger mt-1 flex-shrink-0"></i>
                <div>
                  <p className="fw-semibold mb-1">
                    Read the full article on {data.source}
                  </p>
                  <p className="text-muted small mb-3">
                    This is an external article published by {data.source}.
                    Click below to read the complete feature on their website.
                  </p>
                  <a
                    href={data.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-danger btn-sm"
                  >
                    <i className="mdi mdi-newspaper me-2"></i>
                    Read on {data.source}
                  </a>
                </div>
              </div>
            </div>

            {/* Live X / Twitter embed */}
            {data.tweetId && (
              <div className="card border-0 shadow-sm rounded p-4 mb-4">
                <h6 className="fw-semibold mb-1">
                  <i
                    className="mdi mdi-twitter me-2"
                    style={{ color: "#1da1f2" }}
                  ></i>
                  Shared on X (Twitter)
                </h6>
                <p className="text-muted small mb-3">
                  Live post — reactions and engagement load directly from X.
                </p>
                <div className="d-flex justify-content-center">
                  <blockquote
                    className="twitter-tweet"
                    data-theme="light"
                    data-width="550"
                  >
                    <a href={data.tweetUrl}>Loading post…</a>
                  </blockquote>
                </div>
              </div>
            )}

            {/* About the founder */}
            <div
              className="card border-0 rounded p-4 d-flex flex-row align-items-center gap-4"
              style={{ background: "#fff8f5" }}
            >
              <img
                src={team1}
                alt="Esther Namukwaya"
                className="rounded-circle shadow flex-shrink-0"
                style={{ width: 72, height: 72, objectFit: "cover" }}
              />
              <div>
                <p className="fw-semibold mb-0">
                  Miss. Namukwaya Esther Yawe Ntwatwa
                </p>
                <p className="text-muted small mb-0">
                  Founder, Tu Es Belle · Author · Humanitarian Activist · Health
                  Care Administrator
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Regular blog detail layout
function RegularDetail({ data }) {
  return (
    <section className="section">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-9 col-12">
            <div className="card border-0 shadow rounded overflow-hidden">
              <img
                src={data?.image ?? team1}
                className="img-fluid w-100"
                style={{ maxHeight: 480, objectFit: "cover" }}
                alt={data?.title}
              />
              <p className="text-center text-muted small py-2 mb-0 border-bottom">
                Image of Miss. Namukwaya Esther Yawe Ntwatwa, Founder of Tu Es
                Belle, Davidic Nation and Robin Paul
              </p>

              <div className="card-body p-4 p-lg-5">
                <p className="text-muted">
                  Miss. Esther is a self published author, humanitarian
                  activist, entrepreneur and a health care administrator.
                </p>
                <p className="text-muted">
                  Growing up as a child there was always a thymos for a-lot of
                  things happening that I didn't have the power to change. And
                  was graced with an empowerment from my father and brother who
                  strongly believed in the individuality of women and always
                  gave me a voice to speak up. With a good example of my mother
                  who for one was not only god fearing but also empowered in all
                  aspects. I vividly remember her getting involved in all church
                  and charity activities. Giving away the little we also had
                  while echoing the words; "anyone can make a difference in
                  anyone's life".
                </p>

                <p className="text-muted">
                  And growing up in school I knew I wanted to make a difference
                  in anyway I can. And what better way to explain it in another
                  favorite quote of mine.
                </p>

                <blockquote className="text-center mx-auto blockquote my-4">
                  <i className="mdi mdi-format-quote-open mdi-48px text-muted opacity-2 d-block"></i>
                  Give me an educated mother and I will give you an educated
                  nation.
                  <small className="d-block text-muted mt-2">— Napoleon</small>
                </blockquote>

                <p className="text-muted">
                  That statement is enough to prove the importance of female
                  education and accreditation. Which is the power that all women
                  need to access.
                </p>

                <p className="text-muted">
                  If the women are uneducated then the nation is at stake.
                  Because the opportunity to enrich the society, mold the
                  children and influence the decisions on men is disrupted.
                </p>

                <p className="text-muted">
                  Skills and strong sense of identity and self worth are put on
                  hold because of that. Which is why Tu Es Belle is here to
                  create aspiration by fighting inequality, equipping women with
                  skills not only financial, but mentally, spiritually and
                  emotionally.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Main BlogDetail page
export default function BlogDetail() {
  const params = useParams();
  const data = blogData.find((item) => item.id === parseInt(params.id));
  const isPress = data?.type === "press";

  return (
    <>
      <Navbar />

      {/* Hero banner */}
      <section
        className="bg-half-170 d-table w-100"
        style={{
          backgroundImage: `url(${hero})`,
          backgroundPosition: "center",
        }}
      >
        <div className="bg-overlay bg-gradient-overlay"></div>
        <div className="container">
          <div className="row mt-5 justify-content-center">
            <div className="col-12">
              <div className="title-heading text-center">
                <span
                  className={`badge ${isPress ? "bg-danger" : "bg-primary"}`}
                >
                  {data?.tag ?? "Tu Es Belle"}
                </span>

                <h5 className="heading fw-semibold mb-0 sub-heading text-white title-dark mt-4">
                  {data?.title ?? "Message from the Founder."}
                </h5>

                <ul className="list-inline text-center mb-0">
                  {/* Author — only shown for regular posts */}
                  {!isPress && (
                    <li className="list-inline-item mx-4 mt-4">
                      <span className="text-white-50 d-block">Author</span>
                      <Link to="#" className="text-white title-dark">
                        Miss. Namukwaya Esther Yawe Ntwatwa
                      </Link>
                    </li>
                  )}

                  <li className="list-inline-item mx-4 mt-4">
                    <span className="text-white-50 d-block">Date</span>
                    <span className="text-white title-dark">
                      {data?.date ?? "2nd January, 2025"}
                    </span>
                  </li>

                  {isPress ? (
                    <li className="list-inline-item mx-4 mt-4">
                      <span className="text-white-50 d-block">
                        Published In
                      </span>
                      <span className="text-white title-dark">
                        {data.source}
                      </span>
                    </li>
                  ) : (
                    <li className="list-inline-item mx-4 mt-4">
                      <span className="text-white-50 d-block">Read Time</span>
                      <span className="text-white title-dark">5 min read</span>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          <div className="position-middle-bottom">
            <nav aria-label="breadcrumb" className="d-block">
              <ul className="breadcrumb breadcrumb-muted mb-0 p-0">
                <li className="breadcrumb-item">
                  <Link to="/">Tu Es Belle</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/blogs">Blogs</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {isPress ? "Press Feature" : "Article"}
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </section>

      <div className="position-relative">
        <div className="shape overflow-hidden text-white">
          <svg
            viewBox="0 0 2880 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z"
              fill="currentColor"
            ></path>
          </svg>
        </div>
      </div>

      {/* Route to correct layout */}
      {isPress ? <PressDetail data={data} /> : <RegularDetail data={data} />}

      <Footer />
      <ScrollToTop />
    </>
  );
}
